import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Briefcase,
  Check,
  Calendar,
  TrendingUp,
  Users,
  Clock,
  ArrowLeft,
  Star,
  Zap
} from 'lucide-react';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback } from '../ui/avatar';

export function SubscriptionPortal() {
  const employees = [
    { id: 1, name: 'Sarah Johnson', role: 'Care Assistant', performance: 92, absences: 2, holidayDays: 5 },
    { id: 2, name: 'Michael Chen', role: 'Support Worker', performance: 88, absences: 1, holidayDays: 8 },
    { id: 3, name: 'Emma Williams', role: 'Healthcare Assistant', performance: 95, absences: 0, holidayDays: 3 },
  ];

  const plans = [
    {
      name: 'Starter',
      price: 'Free',
      description: 'Perfect for small businesses',
      features: [
        '1 job posting per month',
        'Basic candidate search',
        'Email support',
        'Job posting for 30 days',
      ],
      current: false,
    },
    {
      name: 'Professional',
      price: '£99/month',
      description: 'For growing companies',
      features: [
        'Unlimited job postings',
        'AI candidate matching',
        'Priority support',
        'Advanced analytics',
        'Featured job listings',
        'Candidate messaging',
        'Skill testing tools',
      ],
      current: true,
      popular: true,
    },
    {
      name: 'Enterprise',
      price: '£299/month',
      description: 'Full team management suite',
      features: [
        'Everything in Professional',
        'Team management tools',
        'Absence tracking',
        'Performance monitoring',
        'Holiday planner',
        'Custom integrations',
        'Dedicated account manager',
        'White-label options',
      ],
      current: false,
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">
                Lighthouse Jobs
              </span>
            </Link>
            
            <Link to="/employer/dashboard">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="mb-4 text-gold-gradient">
            Subscription & Team Management
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Upgrade your plan to unlock powerful features for managing your team and hiring process
          </p>
        </div>

        <Tabs defaultValue="plans" className="mb-12">
          <TabsList className="bg-white/5 border border-white/10 mb-8">
            <TabsTrigger value="plans" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Star className="w-4 h-4 mr-2" />
              Plans & Pricing
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              Team Management
            </TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {plans.map((plan, index) => (
                <Card
                  key={index}
                  className={`bg-white/5 border-white/10 relative ${
                    plan.popular
                      ? 'ring-2 ring-[#d2ac47] shadow-[0_0_40px_rgba(210,172,71,0.4)]'
                      : 'hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)]'
                  } transition-all duration-300`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-gold-gradient text-black border-0">
                        <Zap className="w-3 h-3 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-white">{plan.name}</CardTitle>
                    <CardDescription className="text-gray-400">
                      {plan.description}
                    </CardDescription>
                    <div className="pt-4">
                      <span className="text-gold-gradient">
                        {plan.price}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                          <Check className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full ${
                        plan.current
                          ? 'bg-white/10 hover:bg-white/20 border border-white/20'
                          : plan.popular
                          ? 'bg-gold-gradient hover:brightness-110 text-black'
                          : 'border border-white/20 hover:bg-white/5'
                      }`}
                      variant={plan.current || !plan.popular ? 'outline' : 'default'}
                      disabled={plan.current}
                    >
                      {plan.current ? 'Current Plan' : 'Upgrade Now'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gradient-to-br from-[#edc967]/10 to-transparent border-[#d2ac47]/20">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gold-gradient flex items-center justify-center flex-shrink-0">
                    <Zap className="w-8 h-8 text-black" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-white mb-2">Need a custom solution?</h3>
                    <p className="text-gray-300">
                      Contact our sales team for enterprise pricing and custom integrations tailored to your business needs.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10 flex-shrink-0"
                  >
                    Contact Sales
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Management Tab */}
          <TabsContent value="team">
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Total Employees', value: '12', icon: Users },
                { label: 'Avg Performance', value: '91%', icon: TrendingUp },
                { label: 'Total Absences', value: '5', icon: Clock },
                { label: 'Pending Holidays', value: '8', icon: Calendar },
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card
                    key={index}
                    className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center">
                          <Icon className="w-6 h-6 text-black" />
                        </div>
                      </div>
                      <div className="text-white mb-1">{stat.value}</div>
                      <p className="text-sm text-gray-400">{stat.label}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="bg-white/5 border-white/10 mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Team Overview</CardTitle>
                    <CardDescription className="text-gray-400">
                      Track performance, absences, and holidays
                    </CardDescription>
                  </div>
                  <Button className="bg-gold-gradient hover:brightness-110 text-black">
                    Add Employee
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {employees.map((employee) => (
                    <Card
                      key={employee.id}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all"
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row gap-6">
                          <div className="flex items-start gap-4 flex-1">
                            <Avatar className="w-12 h-12">
                              <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                                {employee.name.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <h4 className="text-white mb-1">{employee.name}</h4>
                              <p className="text-sm text-gray-400">{employee.role}</p>
                            </div>
                          </div>

                          <div className="flex-1 grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Performance</p>
                              <div className="mb-1 text-white">{employee.performance}%</div>
                              <Progress value={employee.performance} className="h-2" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Absences (YTD)</p>
                              <div className="text-white">{employee.absences} days</div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-2">Holiday Taken</p>
                              <div className="text-white">{employee.holidayDays} days</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-white/20 text-white hover:bg-white/5"
                            >
                              View Details
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#ba9036]" />
                    Holiday Planner
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Manage team holiday requests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { name: 'Sarah Johnson', dates: 'Dec 20-27', status: 'Approved' },
                      { name: 'Michael Chen', dates: 'Jan 5-12', status: 'Pending' },
                      { name: 'Emma Williams', dates: 'Feb 1-5', status: 'Approved' },
                    ].map((holiday, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-white/5 rounded-lg border border-white/10 flex items-center justify-between"
                      >
                        <div>
                          <p className="text-white text-sm mb-1">{holiday.name}</p>
                          <p className="text-xs text-gray-400">{holiday.dates}</p>
                        </div>
                        <Badge
                          className={
                            holiday.status === 'Approved'
                              ? 'bg-green-500/20 text-green-400 border-green-500/50'
                              : 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                          }
                        >
                          {holiday.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#ba9036]" />
                    Recent Absences
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Track team attendance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { name: 'Sarah Johnson', date: 'Oct 20', reason: 'Sick Leave', status: 'Approved' },
                      { name: 'Michael Chen', date: 'Oct 18', reason: 'Personal', status: 'Approved' },
                      { name: 'Emma Williams', date: 'Oct 15', reason: 'Medical', status: 'Approved' },
                    ].map((absence, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-white/5 rounded-lg border border-white/10"
                      >
                        <div className="flex items-start justify-between mb-1">
                          <p className="text-white text-sm">{absence.name}</p>
                          <span className="text-xs text-gray-400">{absence.date}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-400">{absence.reason}</p>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                            {absence.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-gradient-to-br from-[#edc967]/10 to-transparent border-[#d2ac47]/20 mt-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gold-gradient flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h4 className="text-white mb-2">Upgrade to Enterprise</h4>
                    <p className="text-sm text-gray-300 mb-3">
                      These team management features are available in the Enterprise plan. Upgrade now to access advanced tracking, analytics, and more.
                    </p>
                    <Button className="bg-gold-gradient hover:brightness-110 text-black">
                      Upgrade to Enterprise
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
